"use client";

import classNames from "classnames";
import {
  Children,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useOnClickOutside } from "usehooks-ts";
import { Fieldset } from "../fieldset";
import styles from "./Select.module.scss";
import { SelectTypes } from "./Select.types";
import { ChevronDownIcon } from "@vetty/icons";

type SelectOption = {
  value: string;
  label: string;
  disabled: boolean;
};

const TYPEAHEAD_RESET_MS = 500;
const MENU_OFFSET = 10;
const MENU_Z_INDEX = 6000;

const Select = ({
  id,
  name,
  size = "medium",
  variant = "primary",
  children,
  className = "",
  placeholder,
  value,
  defaultValue,
  startContent = null,
  endContent = null,
  disabled = false,
  readOnly = false,
  onChange,
  onBlur,
  dropdownPlacement = "bottom",
  fullWidth = false,
  label,
  required = false,
  labelPlacement = "outside",
  error,
  ...buttonProps
}: SelectTypes) => {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const listboxId = `${controlId}-listbox`;
  const wrapperRef = useRef<HTMLDivElement>(null!);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const typeaheadRef = useRef({ query: "", timeoutId: 0 });

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const options = useMemo<SelectOption[]>(() => {
    return Children.toArray(children).flatMap((child) => {
      if (!isValidElement(child) || child.type !== "option") return [];

      const option = child as ReactElement<{
        value?: string;
        children?: string;
        disabled?: boolean;
      }>;

      return [
        {
          value: String(option.props.value ?? ""),
          label: Children.toArray(option.props.children).join(""),
          disabled: Boolean(option.props.disabled),
        },
      ];
    });
  }, [children]);

  const enabledIndexes = useMemo(
    () =>
      options
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => !option.disabled)
        .map(({ index }) => index),
    [options]
  );

  const getInitialValue = () => {
    if (value !== undefined) return value;
    if (defaultValue !== undefined) return defaultValue;
    if (placeholder !== undefined) return "";
    return options.find((option) => !option.disabled)?.value ?? "";
  };

  const [internalValue, setInternalValue] = useState(getInitialValue);

  useEffect(() => {
    if (value === undefined) return;
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (value !== undefined) return;
    if (options.some((option) => option.value === internalValue)) return;
    if (placeholder !== undefined && internalValue === "") return;

    const nextValue =
      defaultValue ??
      (placeholder !== undefined
        ? ""
        : options.find((option) => !option.disabled)?.value ?? "");

    setInternalValue(nextValue);
  }, [defaultValue, internalValue, options, placeholder, value]);

  const closeMenu = useCallback((returnFocus = false) => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  useOnClickOutside(wrapperRef, (event) => {
    if (menuRef.current?.contains(event.target as Node)) return;
    if (isOpen) closeMenu(false);
  });

  const selectedValue = value ?? internalValue;
  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );
  const valueLabel = selectedOption?.label ?? placeholder ?? "";
  const selectedIndex = options.findIndex(
    (option) => option.value === selectedValue
  );

  const handleSelect = useCallback(
    (nextValue: string) => {
      if (readOnly || disabled) return;
      if (value === undefined) setInternalValue(nextValue);
      onChange?.(nextValue);
      closeMenu(true);
    },
    [closeMenu, disabled, onChange, readOnly, value]
  );

  const moveHighlight = useCallback(
    (direction: 1 | -1 | "home" | "end") => {
      if (enabledIndexes.length === 0) return;

      setHighlightedIndex((current) => {
        if (direction === "home") return enabledIndexes[0];
        if (direction === "end") return enabledIndexes[enabledIndexes.length - 1];

        const currentEnabledPos = enabledIndexes.indexOf(current);
        if (currentEnabledPos === -1) {
          return direction === 1
            ? enabledIndexes[0]
            : enabledIndexes[enabledIndexes.length - 1];
        }

        const nextPos =
          (currentEnabledPos + direction + enabledIndexes.length) %
          enabledIndexes.length;
        return enabledIndexes[nextPos];
      });
    },
    [enabledIndexes]
  );

  const openMenu = useCallback(() => {
    if (disabled || readOnly) return;
    setIsOpen(true);
    const start =
      selectedIndex >= 0 && !options[selectedIndex]?.disabled
        ? selectedIndex
        : enabledIndexes[0] ?? -1;
    setHighlightedIndex(start);
  }, [disabled, enabledIndexes, options, readOnly, selectedIndex]);

  const handleTypeahead = useCallback(
    (key: string) => {
      if (key.length !== 1 || !/\S/.test(key)) return false;

      window.clearTimeout(typeaheadRef.current.timeoutId);
      typeaheadRef.current.query += key.toLowerCase();
      typeaheadRef.current.timeoutId = window.setTimeout(() => {
        typeaheadRef.current.query = "";
      }, TYPEAHEAD_RESET_MS);

      const query = typeaheadRef.current.query;
      const startFrom =
        highlightedIndex >= 0 ? highlightedIndex + 1 : selectedIndex + 1;

      for (let offset = 0; offset < options.length; offset += 1) {
        const index = (startFrom + offset) % options.length;
        const option = options[index];
        if (option.disabled) continue;
        if (option.label.toLowerCase().startsWith(query)) {
          if (isOpen) {
            setHighlightedIndex(index);
          } else if (!readOnly && !disabled) {
            if (value === undefined) setInternalValue(option.value);
            onChange?.(option.value);
          }
          return true;
        }
      }

      return true;
    },
    [
      disabled,
      highlightedIndex,
      isOpen,
      onChange,
      options,
      readOnly,
      selectedIndex,
      value,
    ]
  );

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const rootEl = rootRef.current;
      const menuEl = menuRef.current;
      if (!rootEl || !menuEl) return;

      const rootRect = rootEl.getBoundingClientRect();
      const menuHeight = menuEl.offsetHeight;
      const spaceBelow = window.innerHeight - rootRect.bottom;
      const spaceAbove = rootRect.top;

      let placement = dropdownPlacement;
      if (placement === "bottom" && spaceBelow < menuHeight + MENU_OFFSET) {
        if (spaceAbove > spaceBelow) placement = "top";
      } else if (placement === "top" && spaceAbove < menuHeight + MENU_OFFSET) {
        if (spaceBelow >= spaceAbove) placement = "bottom";
      }

      const top =
        placement === "top"
          ? rootRect.top - menuHeight - MENU_OFFSET
          : rootRect.bottom + MENU_OFFSET;

      // Exact border-box width of the visible control chrome (not option content).
      const width = rootRect.width;
      let left = rootRect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      left = Math.max(8, left);

      setMenuStyle({
        position: "fixed",
        top: `${Math.max(8, top)}px`,
        left: `${left}px`,
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        boxSizing: "border-box",
        zIndex: MENU_Z_INDEX,
        maxHeight: "min(280px, 60vh)",
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    const rootEl = rootRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && rootEl
        ? new ResizeObserver(() => updatePosition())
        : null;
    if (rootEl && resizeObserver) resizeObserver.observe(rootEl);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      resizeObserver?.disconnect();
    };
  }, [dropdownPlacement, isOpen, options.length]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const optionId = `${controlId}-option-${highlightedIndex}`;
    const optionEl = document.getElementById(optionId);
    optionEl?.scrollIntoView?.({ block: "nearest" });
  }, [controlId, highlightedIndex, isOpen]);

  useEffect(() => {
    return () => {
      window.clearTimeout(typeaheadRef.current.timeoutId);
    };
  }, []);

  const handleRootClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (disabled || readOnly) return;

    const target = event.target as HTMLElement;
    if (target.closest('[data-select-field="true"]')) return;
    if (target.closest('[data-select-dropdown="true"]')) return;

    if (isOpen) closeMenu(false);
    else openMenu();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || readOnly) return;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!isOpen) openMenu();
        else moveHighlight(1);
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!isOpen) openMenu();
        else moveHighlight(-1);
        return;
      }
      case "Home": {
        if (!isOpen) return;
        event.preventDefault();
        moveHighlight("home");
        return;
      }
      case "End": {
        if (!isOpen) return;
        event.preventDefault();
        moveHighlight("end");
        return;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          return;
        }
        const option = options[highlightedIndex];
        if (option && !option.disabled) handleSelect(option.value);
        return;
      }
      case "Escape": {
        if (!isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        closeMenu(true);
        return;
      }
      case "Tab": {
        if (isOpen) closeMenu(false);
        return;
      }
      default: {
        if (handleTypeahead(event.key)) {
          event.preventDefault();
        }
      }
    }
  };

  const hasStartContent = startContent !== null;
  const activeDescendant =
    isOpen && highlightedIndex >= 0
      ? `${controlId}-option-${highlightedIndex}`
      : undefined;

  const selectInner = (
    <div
      ref={rootRef}
      onClick={handleRootClick}
      className={classNames(
        styles["comp"],
        styles[`comp--variant-${variant}`],
        styles[`comp--size-${size}`],
        isOpen && styles["comp--open"],
        disabled && styles[`comp--disabled`],
        readOnly && styles[`comp--readonly`],
        fullWidth && styles["comp--full-width"]
      )}
    >
      {name && <input type="hidden" name={name} value={selectedValue} />}

      {hasStartContent && (
        <span className={styles["comp__content"]} aria-hidden="true">
          {startContent}
        </span>
      )}

      <button
        id={controlId}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        aria-readonly={readOnly || undefined}
        data-select-field="true"
        onClick={(event) => {
          event.stopPropagation();
          if (isOpen) closeMenu(false);
          else openMenu();
        }}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        className={styles["comp__field"]}
        {...buttonProps}
      >
        <span
          className={classNames(
            styles["comp__value"],
            !selectedOption && styles["comp__value--placeholder"]
          )}
        >
          {valueLabel}
        </span>
      </button>

      <span
        className={classNames(
          styles["comp__content"],
          styles["comp__content--end"],
          isOpen && styles["comp__content--open"]
        )}
        aria-hidden="true"
      >
        {endContent}
        <ChevronDownIcon style={{ width: 20, height: 20 }} />
      </span>
    </div>
  );

  const menu =
    isOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            data-select-dropdown="true"
            className={classNames(
              styles["comp__dropdown"],
              styles["comp__dropdown--portal"],
              styles[`comp__dropdown--variant-${variant}`],
              styles[`comp__dropdown--size-${size}`]
            )}
            style={menuStyle}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              id={listboxId}
              role="listbox"
              aria-labelledby={controlId}
              className={styles["comp__dropdown-scroll"]}
            >
              {options.map((option, index) => (
                <button
                  key={`${option.value}-${index}`}
                  id={`${controlId}-option-${index}`}
                  type="button"
                  role="option"
                  disabled={option.disabled}
                  aria-selected={selectedValue === option.value}
                  tabIndex={-1}
                  className={classNames(
                    styles["comp__option"],
                    selectedValue === option.value &&
                      styles["comp__option--selected"],
                    highlightedIndex === index &&
                      styles["comp__option--highlighted"]
                  )}
                  onMouseEnter={() => {
                    if (!option.disabled) setHighlightedIndex(index);
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect(option.value);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <Fieldset
      error={error}
      rootClassName={classNames(
        styles["comp-fieldset"],
        fullWidth && styles["comp-fieldset--full-width"]
      )}
    >
      <div
        ref={wrapperRef}
        className={classNames(
          styles["comp-wrap"],
          fullWidth && styles["comp-wrap--full-width"],
          className
        )}
      >
        {label && labelPlacement === "outside" && (
          <label className={styles["comp-wrap__label"]} htmlFor={controlId}>
            <span>{label}</span>
            {required ? <span>*</span> : null}
          </label>
        )}
        {selectInner}
        {menu}
      </div>
    </Fieldset>
  );
};

export { Select };
